/**
 * 路径锚点模型（M3-15 / DESIGN.md §6.3）。
 *
 * 钢笔工具编辑的是**锚点 + 控制柄**，不是 SVG 字符串——字符串只是持久化
 * 格式（PathData.commands，M1-06 就定好的契约）。角点没有控制柄
 * （前后控制柄与锚点重合，退化为直线段）；平滑点的前后控制柄关于锚点对称。
 */

export interface Point {
  readonly x: number;
  readonly y: number;
}

/** 一个锚点：位置 + 可选的入/出控制柄（都是**绝对坐标**，不是相对偏移）。 */
export interface Anchor {
  readonly point: Point;
  /** 入方向控制柄（来自上一段的曲线控制点）。undefined = 角点，前段为直线。 */
  readonly handleIn?: Point;
  /** 出方向控制柄（去往下一段的曲线控制点）。undefined = 角点，后段为直线。 */
  readonly handleOut?: Point;
}

export interface Path {
  readonly anchors: readonly Anchor[];
  readonly closed: boolean;
  readonly fillRule: 'nonzero' | 'evenodd';
}

/** 把一个锚点的出控制柄按镜像规则设置：out = point + (point - dragTo)，令入/出对称（平滑点）。 */
export function mirrorHandle(anchor: Point, dragTo: Point): Point {
  return { x: anchor.x * 2 - dragTo.x, y: anchor.y * 2 - dragTo.y };
}

/** 序列化为 SVG path 子集（M/L/C，绝对坐标）。 */
export function pathToCommands(path: Path): string {
  const { anchors } = path;
  if (anchors.length === 0) return '';
  const parts: string[] = [`M ${fmt(anchors[0]!.point)}`];

  const segmentCount = path.closed ? anchors.length : anchors.length - 1;
  for (let i = 0; i < segmentCount; i++) {
    const from = anchors[i]!;
    const to = anchors[(i + 1) % anchors.length]!;
    const c1 = from.handleOut ?? from.point;
    const c2 = to.handleIn ?? to.point;
    if (from.handleOut === undefined && to.handleIn === undefined) {
      parts.push(`L ${fmt(to.point)}`);
    } else {
      parts.push(`C ${fmt(c1)} ${fmt(c2)} ${fmt(to.point)}`);
    }
  }
  if (path.closed) parts.push('Z');
  return parts.join(' ');
}

/** 矩形的四个角点（顺时针，从左上开始），全是角点（无控制柄）——直边不需要曲线。 */
export function rectangleAnchors(x0: number, y0: number, x1: number, y1: number): Anchor[] {
  return [
    { point: { x: x0, y: y0 } },
    { point: { x: x1, y: y0 } },
    { point: { x: x1, y: y1 } },
    { point: { x: x0, y: y1 } },
  ];
}

/** 四段三次贝塞尔近似圆/椭圆的标准控制柄长度系数（半径的 4(√2−1)/3）。 */
export const ELLIPSE_KAPPA = 0.5522847498;

/**
 * 圆角矩形（矢量形状工具的"半径"选项，参照 Photoshop）。半径 ≤ 0 时
 * 退化为 {@link rectangleAnchors} 的四个角点——不为零半径生成四段
 * 长度为零的贝塞尔曲线，那样会产出一堆退化控制柄，纯属浪费还容易在
 * 别处（比如控制柄编辑）露出奇怪的 0 长度手柄。
 *
 * 每个角是一段四分之一圆的三次贝塞尔近似，跟 {@link ellipseAnchors}
 * 同一套"控制柄沿本地切线方向、长度 r×ELLIPSE_KAPPA"手法——只是这里
 * 四个角各自的切线方向就是它所在那条直边的方向（角的两个端点各自落在
 * 一条直边上，切线自然跟那条边平行），不用像椭圆那样为每段单独算角度。
 * 半径超过短边一半时夹到 `min(width, height) / 2`，避免四个圆弧互相
 * 咬穿产出自相矛盾的路径（跟 `fromRoundedRect` 选区那边同一个夹取规则，
 * 两处保持一致不是巧合——都是"圆角矩形"这同一个几何概念）。
 */
export function roundedRectangleAnchors(
  x0: number,
  y0: number,
  x1: number,
  y1: number,
  radius: number,
): Anchor[] {
  if (radius <= 0) return rectangleAnchors(x0, y0, x1, y1);
  const r = Math.min(radius, (x1 - x0) / 2, (y1 - y0) / 2);
  const k = r * ELLIPSE_KAPPA;
  return [
    { point: { x: x0 + r, y: y0 }, handleIn: { x: x0 + r - k, y: y0 } }, // 上边左端：左上角弧的终点
    { point: { x: x1 - r, y: y0 }, handleOut: { x: x1 - r + k, y: y0 } }, // 上边右端：右上角弧的起点
    { point: { x: x1, y: y0 + r }, handleIn: { x: x1, y: y0 + r - k } }, // 右边上端：右上角弧的终点
    { point: { x: x1, y: y1 - r }, handleOut: { x: x1, y: y1 - r + k } }, // 右边下端：右下角弧的起点
    { point: { x: x1 - r, y: y1 }, handleIn: { x: x1 - r + k, y: y1 } }, // 下边右端：右下角弧的终点
    { point: { x: x0 + r, y: y1 }, handleOut: { x: x0 + r - k, y: y1 } }, // 下边左端：左下角弧的起点
    { point: { x: x0, y: y1 - r }, handleIn: { x: x0, y: y1 - r + k } }, // 左边下端：左下角弧的终点
    { point: { x: x0, y: y0 + r }, handleOut: { x: x0, y: y0 + r - k } }, // 左边上端：左上角弧的起点（闭合回第一个点）
  ];
}

/** 椭圆的四段三次贝塞尔近似（圆是 rx===ry 的特例），从正上方顶点顺时针排列。 */
export function ellipseAnchors(cx: number, cy: number, rx: number, ry: number): Anchor[] {
  const k = ELLIPSE_KAPPA;
  const top = { x: cx, y: cy - ry };
  const right = { x: cx + rx, y: cy };
  const bottom = { x: cx, y: cy + ry };
  const left = { x: cx - rx, y: cy };
  return [
    { point: top, handleOut: { x: cx + rx * k, y: cy - ry }, handleIn: { x: cx - rx * k, y: cy - ry } },
    { point: right, handleOut: { x: cx + rx, y: cy + ry * k }, handleIn: { x: cx + rx, y: cy - ry * k } },
    { point: bottom, handleOut: { x: cx - rx * k, y: cy + ry }, handleIn: { x: cx + rx * k, y: cy + ry } },
    { point: left, handleOut: { x: cx - rx, y: cy - ry * k }, handleIn: { x: cx - rx, y: cy + ry * k } },
  ];
}

/**
 * 直线的四个角点，围成一条沿 from→to 方向、宽度为 `weight` 的细长矩形。
 * Photoshop 的直线形状本身就是这么实现的——一个填色的细长四边形，
 * 不是真的沿路径描边（本项目的形状图层合成目前也只支持填充，见
 * app/composite.ts 的 compositeShape，没有独立的描边栅格化）。
 * `from === to`（零长度）时退化成一个 weight×weight 的方块，不产生零面积路径。
 */
export function lineAnchors(from: Point, to: Point, weight: number): Anchor[] {
  const dx = to.x - from.x;
  const dy = to.y - from.y;
  const len = Math.hypot(dx, dy);
  const half = weight / 2;
  if (len === 0) return rectangleAnchors(from.x - half, from.y - half, from.x + half, from.y + half);
  const nx = (-dy / len) * half;
  const ny = (dx / len) * half;
  return [
    { point: { x: from.x + nx, y: from.y + ny } },
    { point: { x: to.x + nx, y: to.y + ny } },
    { point: { x: to.x - nx, y: to.y - ny } },
    { point: { x: from.x - nx, y: from.y - ny } },
  ];
}

function fmt(p: Point): string {
  return `${round(p.x)} ${round(p.y)}`;
}

/** 保留 3 位小数——够用且序列化字符串不至于过长。 */
function round(n: number): number {
  return Math.round(n * 1000) / 1000;
}

/**
 * 解析 M/L/C/Q/Z 子集（绝对坐标，本项目从不写相对坐标，解析器也不必兼容）。
 * 防御性：token 数量与命令语义不匹配时抛错而不是静默吞掉（§13.5）。
 */
export function parseCommands(commands: string): {
  segments: ReadonlyArray<
    | { readonly kind: 'M'; readonly to: Point }
    | { readonly kind: 'L'; readonly to: Point }
    | { readonly kind: 'C'; readonly c1: Point; readonly c2: Point; readonly to: Point }
    | { readonly kind: 'Q'; readonly c: Point; readonly to: Point }
    | { readonly kind: 'Z' }
  >;
} {
  const tokens = commands.trim().split(/[\s,]+/).filter((t) => t.length > 0);
  const segments: Array<
    | { kind: 'M'; to: Point }
    | { kind: 'L'; to: Point }
    | { kind: 'C'; c1: Point; c2: Point; to: Point }
    | { kind: 'Q'; c: Point; to: Point }
    | { kind: 'Z' }
  > = [];
  let i = 0;
  const num = (): number => {
    const v = Number(tokens[i++]);
    if (!Number.isFinite(v)) throw new RangeError(`路径命令包含非法数值（位置 ${i - 1}）`);
    return v;
  };
  const point = (): Point => ({ x: num(), y: num() });

  while (i < tokens.length) {
    const cmd = tokens[i++];
    if (cmd === 'M') segments.push({ kind: 'M', to: point() });
    else if (cmd === 'L') segments.push({ kind: 'L', to: point() });
    else if (cmd === 'C') segments.push({ kind: 'C', c1: point(), c2: point(), to: point() });
    else if (cmd === 'Q') segments.push({ kind: 'Q', c: point(), to: point() });
    else if (cmd === 'Z') segments.push({ kind: 'Z' });
    else throw new RangeError(`不支持的路径命令「${cmd}」`);
  }
  return { segments };
}
